<template>
    <div class="sections-edit-nav-wrapper">
        <div class="apprentice-simple-card">
            <div class="generic-settings">
                <div class="component-title">
                    <h5 class="title">{{ $t('rating.general') }}</h5>
                </div>

                <div class="visibility">
                    <b-form-group
                        class="visibility-setting-group"
                        label-cols="12"
                        label-cols-lg="5"
                        content-cols="12"
                        content-cols-lg="7"
                        label="zichtbaarheid"
                        label-for="visibility-setting-select">
                        <template #label> Zichtbaarheid: <span class="required">*</span> </template>
                        <b-form-select
                            class="apprentice-primary-select"
                            id="visibility-setting-select"
                            v-model="selectedVisibility"
                            :options="visibilityOptions"
                            @change="updateRatingables">
                        </b-form-select>
                        <b-popover
                            custom-class="visibility-error"
                            :show.sync="toggle"
                            triggers="linked-setting-toggle"
                            target="visibility-setting-select"
                            title="Zichtbaarheid">
                            <template #title>
                                <div class="header"><i class="fal fa-exclamation-triangle"></i> Zichtbaarheid</div>
                            </template>
                            <div class="body">Je hebt geen zichtbaarheid optie geselecteerd</div>
                        </b-popover>
                    </b-form-group>
                </div>

                <div class="custom-name">
                    <b-form-group
                        class="custom-name-setting-group"
                        label-cols="12"
                        label-cols-lg="5"
                        content-cols="12"
                        content-cols-lg="7"
                        label="Aangepaste naam"
                        label-for="custom-name-setting-input">
                        <b-form-input
                            class="custom-name-input apprentice-input-field"
                            id="custom-name-setting-input"
                            v-model="customTitle"
                            @blur="updateRatingables">
                        </b-form-input>
                    </b-form-group>
                </div>

                <div class="linked">
                    <b-form-group
                        class="liked-setting-group"
                        label-cols="12"
                        label-cols-lg="10"
                        content-cols="12"
                        content-cols-lg="2"
                        label="Formulier koppelen aan project"
                        label-for="linked-setting-toggle">
                        <b-form-checkbox
                            class="linked-toggle-setting"
                            id="linked-setting-toggle"
                            v-model="linked"
                            switch
                            @change="attachForm">
                        </b-form-checkbox>
                    </b-form-group>
                </div>
            </div>

            <div class="grade-settings">
                <div class="component-title">
                    <h5 class="title">Cijfers</h5>
                </div>

                <b-form-group
                    class="explanation-setting-group"
                    label-cols="12"
                    label-cols-lg="10"
                    content-cols="12"
                    content-cols-lg="2"
                    label="Toelichting op het cijfer"
                    label-for="explanation-setting-switch">
                    <b-form-checkbox
                        id="explanation-setting-switch"
                        v-model="ratingIn.result_explanation"
                        value="1"
                        unchecked-value="0"
                        switch
                        @change="setRatingExplanation">
                    </b-form-checkbox>
                </b-form-group>

                <b-form-group
                    class="negative-points-setting-group"
                    label-cols="12"
                    label-cols-lg="10"
                    content-cols="12"
                    content-cols-lg="2"
                    label="Minpunten ingeven"
                    label-for="negative-points-setting-switch">
                    <b-form-checkbox
                        id="negative-points-setting-switch"
                        v-model="ratingIn.result_penalty"
                        value="1"
                        unchecked-value="0"
                        switch
                        @change="setRatingPenalty">
                    </b-form-checkbox>
                </b-form-group>

                <div class="calculate-grade">
                    <div class="label">Eindcijfer berekening:</div>
                    <span class="label points-total">Aantal punten ({{ totalNotRoundedPoints() }}) /</span>
                    <b-form-group class="results-formula-group">
                        <b-form-input
                            class="results-divided"
                            v-model="ratingIn.result_divide"
                            @change="setRatingDivide">
                        </b-form-input>
                        +
                        <b-form-input
                            class="results-divided"
                            v-model="ratingIn.result_plus"
                            @change="setRatingPlus">
                        </b-form-input>
                    </b-form-group>
                </div>

                <div class="max-grade">
                    <span class="max-grade-label">Maximale cijfer</span>
                    <span class="grade">{{ maxScore() }}</span>
                </div>
            </div>
        </div>

        <div
            class="sections-edit-nav"
            role="tablist">
            <draggable
                group="navparts"
                :list="this.ratingIn.sections"
                handle=".sections-drag-handle"
                @change="sectionsOrderChanged()"
                ghost-class="drag-ghost">
                <design-2021-ratign-edit-items-nav-component
                    v-for="section in this.ratingIn.sections"
                    :key="section.id"
                    :section-in="section"
                    :selected="section.id === selected"
                    @hold="hold($event)"
                    @continue="cont($event)"
                    @selected-rating-section="selectedSectionChange($event)">
                </design-2021-ratign-edit-items-nav-component>
            </draggable>
        </div>

        <div class="add-section">
            <b-button
                class="btn-add btn-add-section"
                v-b-modal="'rating-edit-add-section-modal' + ratingIn.id"
                variant="primary">
                {{ $t('rating.new_section') }}
            </b-button>

            <design-2021-rating-edit-add-section-modal-component
                :rating="ratingIn"></design-2021-rating-edit-add-section-modal-component>
        </div>
    </div>
</template>

<script>
import { ords } from '../../app';
import draggable from 'vuedraggable';

export default {
    name: 'design-2021-rating-edit-sub-nav-component',
    components: {
        draggable,
    },
    props: ['ratingIn', 'groupIn'],
    data() {
        return {
            selected: null,
            holds: [],
            storageName: 'rating-edit-selected-item-' + this.ratingIn.id,
            visibilityOptions: [
                {
                    value: null,
                    text: 'Maak een keuze',
                    disabled: true,
                },
                {
                    value: 1,
                    text: 'Zichtbaar voor iedereen',
                },
                {
                    value: 2,
                    text: 'Zichtbaar alleen voor docenten',
                },
            ],
            selectedVisibility: null,
            linked: false,
            toggle: false,
            customTitle: null,
            loading: true,
        };
    },

    mounted() {
        if (localStorage.getItem(this.storageName)) {
            this.selected = parseInt(localStorage.getItem(this.storageName));
        }

        const ratingable = this.groupIn.ratingables.find((ratingable) => ratingable.rating_id === this.ratingIn.id);
        if (ratingable) {
            this.selectedVisibility = ratingable.type;
            this.customTitle = ratingable.title;
            this.linked = true;
        }
    },

    methods: {
        selectItem(id) {
            localStorage.setItem(this.storageName, id);
            this.$emit('selected-rating-section', id);
        },

        setRatingExplanation(value) {
            const data = {
                result_explanation: value,
            };

            this.axios.post(this.routes.ratingSetResultExplanation(this.ratingIn.id), data).then(
                (response) => {},
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        setRatingPenalty(value) {
            const data = {
                result_penalty: value,
            };

            this.axios.post(this.routes.ratingSetResultPenalty(this.ratingIn.id), data).then(
                (response) => {},
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        setRatingDivide() {
            const data = {
                result_divide: this.ratingIn.result_divide,
            };

            this.axios.post(this.routes.ratingSetResultDivide(this.ratingIn.id), data).then(
                (response) => {},
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        maxScore() {
            var result_plus = parseFloat(this.ratingIn.result_plus);
            if (isNaN(result_plus)) {
                result_plus = 0;
            }

            var result_divide = parseFloat(this.ratingIn.result_divide);
            if (isNaN(result_divide)) {
                result_divide = 1;
            }

            let grade = this.totalNotRoundedPoints() / result_divide + result_plus;

            return Math.round(grade * 100) / 100;
        },

        totalNotRoundedPoints() {
            let total = 0;

            this.ratingIn.sections.forEach(function (section, key) {
                if (section.type === 0) {
                    return total;
                }

                section.items.forEach(function (item, key2) {
                    if (item.type === 1) {
                        total += item.weight * item.max;
                    } else {
                        let max = 0;
                        item.options.forEach(function (option, key3) {
                            max = option.max > max ? option.max : max;
                        });

                        total += item.weight * max;
                    }
                });
            });

            return total;
        },

        setRatingPlus() {
            const data = {
                result_plus: this.ratingIn.result_plus,
            };

            this.axios.post(this.routes.ratingSetResultPlus(this.ratingIn.id), data).then(
                (response) => {},
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        selectedSectionChange(section) {
            this.$emit('selected-rating-section', section);
            this.selected = section;
        },

        sectionsOrderChanged() {
            let blocks = [];

            this.ratingIn.sections.forEach((section, i) => {
                blocks.push({
                    id: section.id,
                    order: i + 1,
                });
            });

            const data = {
                rating_id: this.ratingIn.id,
                blocks: blocks,
            };

            this.axios.post(this.routes.ratingSectionOrder(), data).then(
                (response) => {
                    ords.hold();

                    for (const data of response.data) {
                        for (const section of this.ratingIn.sections) {
                            if (data.id === section.id) {
                                section.update({ order: data.order });
                            }
                        }
                    }

                    ords.continue();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        hold(key) {
            for (const h of this.holds) {
                if (key === h) {
                    return;
                }
            }
            this.holds.push(key);
            ords.hold();
        },

        cont(key) {
            this.holds = this.holds.filter((k) => k !== key);
            if (!this.holds.length) {
                ords.continue();
            }
        },

        attachForm(selected) {
            if (selected && this.selectedVisibility === null) {
                this.toggle = true;
                this.linked = false;
                setTimeout(this.hideMessage, 3000);
                return;
            }

            let isExistingRatingable = false;

            let ratingables = this.groupIn.ratingables.map((ratingable) => {
                let ratingData = {
                    connected: true,
                    ratingable_id: ratingable.id,
                    rating_id: ratingable.rating_id,
                    title: ratingable.title,
                    type: ratingable.type,
                };

                if (ratingable.rating_id === this.ratingIn.id) {
                    ratingData.title = this.customTitle;
                    ratingData.type = this.selectedVisibility;
                    ratingData.connected = selected;
                    isExistingRatingable = true;
                }

                return ratingData;
            });

            if (!isExistingRatingable && selected) {
                ratingables.push({
                    connected: selected,
                    ratingable_id: null,
                    rating_id: this.ratingIn.id,
                    title: this.customTitle,
                    type: this.selectedVisibility,
                });
            }

            let data = null;
            data = new FormData();
            const headers = { 'Content-Type': 'multipart/form-data' };
            let count = 0;

            for (const ratingable of ratingables) {
                if (ratingable.connected) {
                    if (ratingable.ratingable_id) {
                        data.append(`ratingsables[${count}][id]`, ratingable.ratingable_id);
                    }
                    data.append(`ratingsables[${count}][title]`, ratingable.title !== '' ? ratingable.title : null);
                    data.append(`ratingsables[${count}][rating_id]`, ratingable.rating_id);
                    data.append(`ratingsables[${count}][type]`, ratingable.type);
                    count++;
                }
            }

            this.alert.saving();
            this.axios.post(this.routes.ratingGroupAttach(this.groupIn.id), data, { headers: headers }).then(
                (response) => {
                    this.alert.success();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        hideMessage() {
            this.toggle = false;
        },

        updateRatingables() {
            if (this.selectedVisibility && this.linked) {
                this.attachForm(true);
            }
        },
    },
};
</script>

<style scoped lang="scss">
::v-deep label.col-form-label,
input.form-control,
select,
.form-group,
.label {
    margin-bottom: 0;
}

.generic-settings,
.grade-settings {
    > div:not(:last-child) {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.generic-settings {
    margin-bottom: map-get($grid-gutter-spacing, 'sixty');
}

.component-title {
    color: $primary;
    font-weight: $font-weight-bolder;
    border-bottom: 1px solid $border-color;
    padding-bottom: map-get($grid-gutter-spacing, 'eight');
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
}

.calculate-grade ::v-deep {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: row;
    flex-wrap: wrap;

    > div {
        flex: 1 0 100%;
    }

    .form-control {
        width: 50px;
        text-align: center;
    }

    .points-total {
        align-self: center;
    }
}

.results-formula-group ::v-deep {
    margin-left: auto;

    > div {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-direction: row;
        column-gap: map-get($grid-gutter-spacing, 'eight');
    }
}

.max-grade {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
    font-size: 1.125rem;
    font-weight: $font-weight-bold;
    border-top: 1px solid $border-color;
    padding-top: map-get($grid-gutter-spacing, 'fifteen');

    .grade {
        background-color: $primary-darken-alternative;
        padding: map-get($grid-gutter-spacing, 'ten');
        color: $white;
        font-weight: $font-weight-bold;
        border-radius: 8px;
    }
}

.sections-edit-nav-wrapper {
    margin-bottom: map-get($grid-gutter-spacing, 'forty-two');
}

.add-section {
    display: flex;
    align-items: center;
    justify-content: center;

    .btn-add-section {
        margin-top: map-get($grid-gutter-spacing, 'forty-two');
    }
}
</style>
